import { CaretLeft } from '@phosphor-icons/react';
import { useCallback, useRef, type ReactNode, useEffect, type RefObject } from 'react';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useScrollMemory } from '../../hooks/useScrollMemory';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useSwipeBack } from '../../hooks/useSwipeBack';
import { useSession } from '../../store/SessionContext';
import { ServerIllustration } from '../../theme/illustrations';
import { Tirette } from '../ui/Tirette';
import { t } from '../../i18n';

/**
 * Gabarits d'ecran.
 *
 * Deux formes seulement dans toute l'application :
 *  - `ServerScreen` : les cinq onglets, coiffes de l'illustration du spot ;
 *  - `SubScreen`    : tout le reste, avec une barre de retour.
 */

/**
 * Ecran d'onglet.
 * L'illustration du spot occupe le haut, le logo Sirocco est pose
 * dessus, et le contenu remonte par-dessus avec un bord arrondi.
 */
export function ServerScreen({
  children,
  sticky,
  onRefresh,
  footer,
  overlay,
}: {
  children: ReactNode;
  /**
   * Ce qui ne descend pas quand on tire pour rafraichir : la barre de
   * recherche, les commandes d'en-tete. Cela defile normalement avec le
   * reste — seul le geste de tirage les laisse en place.
   */
  sticky?: ReactNode;
  /**
   * Relit l'ecran. Sans elle, le geste de tirage n'existe pas — et le
   * rebond natif reprend la main, qui emporte l'illustration avec lui.
   */
  onRefresh?: () => void | Promise<unknown>;
  /** Element flottant au-dessus de la barre d'onglets (le bouton d'ecriture). */
  footer?: ReactNode;
  /** Contenu par-dessus tout l'ecran : feuille d'actions, voile. */
  overlay?: ReactNode;
}) {
  const { server, ambiance } = useSession();
  /*
    Deux besoins sur le meme noeud : la memoire de defilement le veut en
    ref-fonction — elle doit agir avant la premiere peinture — et le geste
    de tirage le veut en objet, pour y poser ses ecouteurs. On tient donc
    les deux, et une seule ref les alimente.
  */
  const memoire = useScrollMemory<HTMLDivElement>();
  const zone = useRef<HTMLDivElement | null>(null);
  const scrollRef = useCallback(
    (noeud: HTMLDivElement | null) => {
      zone.current = noeud;
      memoire(noeud);
    },
    [memoire],
  );
  const corps = useRef<HTMLDivElement | null>(null);
  const roue = useRef<HTMLSpanElement | null>(null);
  usePullToRefresh(zone, corps, onRefresh, roue);

  useClavierRenduAuDefilement(zone);

  return (
    <div className="server-screen">
      {/*
        L'illustration fait partie du contenu defilant : elle s'efface
        avec les premiers posts au lieu de rester collee en haut.
      */}
      <div ref={scrollRef} className="scroll-area server-screen__scroll pad-tabbar">
        <header className="server-screen__hero">
          <ServerIllustration
            name={server?.theme.illustration ?? 'marrakech'}
            ambiance={ambiance}
          />
        </header>
        {sticky}
        {/*
          Seul ce bloc descend sous le doigt. L'illustration et les
          commandes restent ou elles sont : ce qu'on tire, c'est la liste,
          et la voir glisser sous des reperes immobiles dit d'ou vient le
          contenu qu'on attend.

          Sa position est ecrite directement sur son style pendant le
          geste, hors de React — voir `usePullToRefresh`.
        */}
        {/*
          Derriere le contenu, qui la decouvre en s'ecartant. Seulement la
          ou tirer relit : un onglet sans relecture — les messages — n'a
          pas de roue, meme quand sa liste rebondit.
        */}
        {onRefresh && <Tirette ref={roue} />}
        <div ref={corps} className="server-screen__body">
          {children}
        </div>
      </div>
      {footer}
      {overlay}
    </div>
  );
}

/**
 * Ecran secondaire : fleche de retour, titre, et une action facultative
 * a droite. C'est le gabarit de tous les ecrans annexes.
 */
export function SubScreen({
  title,
  action,
  children,
  onBack,
  /** Barre collee en bas, hors defilement (bouton principal, composeur). */
  bottomBar,
  /**
   * Contenu par-dessus tout l'ecran : feuille d'actions, message
   * ephemere. Il vit hors de la zone defilante, sinon une feuille posee
   * au bas d'un long contenu s'ouvrirait sous le pli.
   */
  overlay,
  /**
   * Relit l'ecran quand on tire la liste vers le bas.
   *
   * Absente, le geste n'existe pas : la plupart des ecrans secondaires
   * n'ont rien a relire, et un contenu qui s'etire pour rien promet une
   * mise a jour qui n'aura pas lieu. Presente, c'est le meme geste que
   * sur le fil et les evenements, au meme endroit.
   */
  onRefresh,
  padded = true,
  /**
   * La roue du feed, plutot que le rebond du systeme.
   *
   * Sur iPhone, un ecran secondaire qu'on tire laisse le systeme etirer
   * la liste : c'est le meme geste que le defilement, sans relais. Mais
   * le systeme ne laisse aucun creux ou la roue puisse paraitre. Un ecran
   * qui veut montrer qu'il relit — la roue qui tourne, le contenu qui
   * glisse dessous — le demande ici, et prend le meme geste que le fil.
   */
  roue = false,
  /**
   * Par ou l'ecran entre.
   *
   * `droite` par defaut : un pas de plus dans la meme direction. `zoom`
   * pour ce qui s'ouvre depuis une tuile qu'on vient de toucher — il
   * grandit a sa place, et le mouvement dit d'ou il sort. Glisser
   * depuis le bord raconterait autre chose.
   */
  entree = 'droite',
  /**
   * Toujours ouvrir en haut, sans reprendre la place quittee.
   *
   * C'est ce qu'il faut a un formulaire : on y entre pour ecrire quelque
   * chose de neuf, et le rouvrir au milieu du questionnaire donne
   * l'impression d'une page a moitie remplie. Les listes, elles, gardent
   * leur place par defaut.
   */
  fromTop = false,
  /**
   * Le haut d'un serveur, pour un ecran qui en est un petit.
   *
   * Pose bord a bord en tete de la zone defilante, sous la barre d'etat,
   * comme l'illustration d'un spot ; la fleche de retour et l'action
   * flottent par-dessus, et le titre ne s'affiche plus — l'ecran le dit
   * lui-meme. C'est la page d'une association.
   */
  hero,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  onBack?: () => void;
  bottomBar?: ReactNode;
  overlay?: ReactNode;
  onRefresh?: () => void | Promise<unknown>;
  padded?: boolean;
  roue?: boolean;
  entree?: 'droite' | 'zoom';
  fromTop?: boolean;
  hero?: ReactNode;
}) {
  const navigate = useNavigate();
  /*
    Le retour se prend aussi au doigt, depuis le bord gauche : c'est le
    chemin inverse de celui par lequel l'ecran est entre. Le bouton reste,
    evidemment — le geste ne se devine pas.
  */
  const back = () => (onBack ? onBack() : navigate(-1));
  const swipe = useSwipeBack(back);

  /*
    Deux besoins sur le meme noeud, comme dans `ServerScreen` : la
    memoire de defilement le veut en ref-fonction, et le champ qui
    remonte au-dessus du clavier le veut en objet, pour y poser son
    ecouteur. Une seule ref alimente les deux.
  */
  const memoire = useScrollMemory<HTMLDivElement>(!fromTop);
  const zone = useRef<HTMLDivElement | null>(null);
  const scrollRef = useCallback(
    (noeud: HTMLDivElement | null) => {
      zone.current = noeud;
      memoire(noeud);
    },
    [memoire],
  );
  /*
    Le meme geste que sur les ecrans d'entree.

    `BrandScreen` le fait depuis toujours pour la connexion et
    l'inscription ; un formulaire d'ici — creer un evenement, changer son
    mot de passe, ecrire a l'aide — a exactement le meme besoin et n'en
    profitait pas. C'est la meme fonction, donc la meme montee.
  */
  useChampAuDessusDuClavier(zone);
  /*
    Et la meme regle que dans les onglets : faire glisser rend l'ecran.
    Chercher un membre a ajouter, puis parcourir les resultats, c'est le
    meme geste que parcourir le fil.
  */
  useClavierRenduAuDefilement(zone);
  /*
    L'ecran ne glisse qu'a l'aller. En revenant, celui-ci etait deja sous
    le doigt pendant tout le balayage : le faire entrer par la droite lui
    ferait rejouer, a l'endroit, le mouvement qu'on vient de lui faire
    subir a l'envers.
  */
  const entrant = useNavigationType() === 'PUSH';

  return (
    <div
      className={`sub-screen${hero ? ' sub-screen--hero' : ''}${
        entrant ? (entree === 'zoom' ? ' sub-screen--zoom' : ' sub-screen--enter') : ''
      }`}
      {...swipe}
    >
      <header className="sub-screen__bar">
        <button className="sub-screen__back" aria-label={t.commun.retour} onClick={back}>
          <CaretLeft className="sens-x" size={22} weight="bold" />
        </button>
        <h1 className="sub-screen__title">{title}</h1>
        <div className="sub-screen__action">{action}</div>
      </header>
      <div
        ref={scrollRef}
        className={`scroll-area sub-screen__body${padded ? ' sub-screen__body--padded' : ''}${
          onRefresh ? ' sub-screen__body--tirable' : ''
        }${onRefresh && roue ? ' sub-screen__body--roue' : ''}`}
      >
        {/* Hors du corps tirable : comme l'illustration d'un spot, il ne
            descend pas sous le doigt. */}
        {hero && <div className="sub-screen__hero">{hero}</div>}
        {onRefresh ? (
          <CorpsTirable zone={zone} onRefresh={onRefresh}>
            {children}
          </CorpsTirable>
        ) : (
          children
        )}
      </div>
      {bottomBar && <div className="sub-screen__bottom">{bottomBar}</div>}
      {overlay}
    </div>
  );
}

/**
 * Le contenu d'un ecran secondaire, tirable vers le bas.
 *
 * A part, et monte seulement quand l'ecran demande le geste : le crochet
 * pose ses ecouteurs sur la zone defilante des qu'il existe, et tous les
 * ecrans secondaires n'ont pas a les porter.
 *
 * C'est ce bloc-la qui descend sous le doigt, comme le corps d'un ecran
 * d'onglet — la barre de titre, elle, ne bouge pas.
 */
function CorpsTirable({
  zone,
  onRefresh,
  children,
}: {
  zone: RefObject<HTMLElement | null>;
  onRefresh: () => void | Promise<unknown>;
  children: ReactNode;
}) {
  const corps = useRef<HTMLDivElement>(null);
  const roue = useRef<HTMLSpanElement | null>(null);
  usePullToRefresh(zone, corps, onRefresh, roue);
  return (
    <>
      <Tirette ref={roue} />
      <div ref={corps} className="sub-screen__corps">
        {children}
      </div>
    </>
  );
}

/**
 * Ecran de marque : degrade cyan, aucune illustration de spot.
 * Connexion, inscription et choix du spot l'utilisent.
 */
export function BrandScreen({ children }: { children: ReactNode }) {
  const hote = useRef<HTMLDivElement>(null);
  useChampAuDessusDuClavier(hote);
  return (
    <div ref={hote} className="brand-screen">
      {children}
    </div>
  );
}

/**
 * Faire glisser la liste rend l'ecran.
 *
 * On tape un mot, les resultats arrivent — et le clavier tient encore la
 * moitie basse. Le geste qui dit « j'ai fini d'ecrire, je regarde »,
 * c'est celui de faire defiler ; demander en plus un appui pour ranger
 * le clavier est un geste de trop. Les listes du systeme le font depuis
 * toujours.
 *
 * **Seulement un champ de cette zone-la.** Le composeur d'un fil de
 * commentaires vit sous la zone defilante, pas dedans : on y fait
 * defiler pour relire un message plus haut, sans vouloir refermer ce
 * qu'on est en train d'ecrire. La meme raison vaut pour le fil d'une
 * conversation, qui a son propre gabarit.
 *
 * Au doigt seulement. A la molette ou au pave tactile, on parcourt
 * volontiers une liste sans perdre le champ qu'on remplit — et ces
 * deux-la ne produisent pas de `touchmove`.
 */
function useClavierRenduAuDefilement(zone: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const noeud = zone.current;
    if (!noeud) return;

    const rendreLEcran = () => {
      const actif = document.activeElement;
      const saisie = actif instanceof HTMLInputElement || actif instanceof HTMLTextAreaElement;
      if (saisie && noeud.contains(actif)) actif.blur();
    };

    noeud.addEventListener('touchmove', rendreLEcran, { passive: true });
    return () => noeud.removeEventListener('touchmove', rendreLEcran);
  }, [zone]);
}

/**
 * Le champ touche remonte au-dessus du clavier.
 *
 * Les deux gabarits s'en servent : l'ecran de marque pour la connexion
 * et l'inscription, l'ecran secondaire pour les formulaires de
 * l'interieur. Un seul mouvement, donc, partout ou l'on tape.
 *
 * `#root` se retire de la hauteur du clavier, ce qui raccourcit la zone
 * defilante — mais ne deplace rien dedans. Un champ situe bas dans le
 * formulaire passait donc sous le clavier au moment meme ou l'on venait
 * d'appuyer dessus, et il fallait tirer la page pour le retrouver. Le
 * geste marchait, mais c'est un geste de trop pour ecrire son adresse.
 *
 * On attend que le clavier soit monte avant de mesurer : avant, la zone a
 * encore toute sa hauteur et il n'y aurait rien a rattraper. Le delai est
 * celui de la montee, plus une image.
 *
 * `scrollTop` plutot que `scrollIntoView` : celui-ci fait defiler tous
 * les ancetres, y compris la vue web elle-meme, qui part alors sous le
 * clavier — la correction devient le probleme.
 */
function useChampAuDessusDuClavier(hote: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const zone = hote.current;
    if (!zone) return;

    let minuteur: number | undefined;

    function remonter(champ: HTMLElement) {
      if (!zone || !champ.isConnected) return;
      const cadre = zone.getBoundingClientRect();
      const cible = champ.getBoundingClientRect();
      /*
        De l'air sous le champ, et largement.

        Vingt-quatre pixels le posaient juste au ras du clavier : on ne
        voyait ni le champ suivant ni le bouton, et rien ne disait que le
        formulaire continuait. A cette distance, ce qui vient apres se
        devine.
      */
      const debord = cible.bottom + 96 - cadre.bottom;
      if (debord <= 0) return;
      /*
        La page se leve, elle ne saute pas.

        Un `scrollTop` pose d'un coup faisait apparaitre le formulaire
        deja deplace : on ne voyait pas le mouvement, seulement son
        resultat, et l'ecran semblait s'etre reinitialise. Le defilement
        doux le rattache au clavier qui monte au meme moment.
      */
      zone.scrollTo({ top: zone.scrollTop + debord, behavior: 'smooth' });
    }

    function surFocus(evenement: FocusEvent) {
      const champ = evenement.target;
      if (!(champ instanceof HTMLElement)) return;
      if (!champ.matches('input, textarea')) return;
      window.clearTimeout(minuteur);
      minuteur = window.setTimeout(() => remonter(champ), 320);
    }

    zone.addEventListener('focusin', surFocus);
    return () => {
      zone.removeEventListener('focusin', surFocus);
      window.clearTimeout(minuteur);
    };
  }, [hote]);
}

/** Voile de chargement, le temps qu'un ecran recoive ses donnees. */
/**
 * L'attente : le S de Sirocco qui respire.
 *
 * Il remplace trois points qui sautillaient. Il s'eleve en s'emplissant
 * et en s'allumant, comme une inspiration, puis redescend. Le dessin est
 * celui du logo, peint a la couleur du spot par un masque : de nuit, il
 * suit.
 */
export function ScreenLoader({ tone = 'spot' }: { tone?: 'spot' | 'brand' }) {
  return (
    <div className="screen-loader">
      <span className={`screen-loader__s screen-loader__s--${tone}`} />
    </div>
  );
}

/** Message d'erreur de chargement, avec une invitation a reessayer. */
export function ScreenError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="screen-error">
      <p>{message}</p>
      {onRetry && (
        <button className="screen-error__retry" onClick={onRetry}>
          {t.commun.reessayer}
        </button>
      )}
    </div>
  );
}
